// src/components/CookieConsent.tsx
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/hooks/useLanguage';
import { useCookieConsent } from '@/hooks/useCookieConsent';
import { m } from 'motion/react';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { localePath } from '@/i18n/paths';
import { CONSENT_BANNER_COPY } from '@bookkeeper/shared/site-chrome';

// The copy is shared with the interpretation hub's banner: one consent cookie, one wording.
export function CookieConsent() {
  const lang = useLanguage();
  const { status, isLoading, accept, decline } = useCookieConsent();
  const shouldReduceMotion = useReducedMotion();
  const text = CONSENT_BANNER_COPY[lang];

  // Don't render during SSR or while loading to prevent hydration mismatch
  if (isLoading) return null;

  // Gone the moment a choice is made: no exit animation, so the banner is
  // never on screen after the click (a spring exit left it there ~0.5 s).
  if (status !== 'pending') return null;

  return (
    <m.div
      initial={shouldReduceMotion ? { opacity: 0 } : { y: 100, opacity: 0 }}
      animate={shouldReduceMotion ? { opacity: 1 } : { y: 0, opacity: 1 }}
      transition={{
        type: 'spring',
        stiffness: 400,
        damping: 30,
      }}
      className="fixed bottom-0 left-0 right-0 z-40 border-t bg-background/95 backdrop-blur-sm px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] shadow-lg md:bottom-4 md:left-4 md:right-4 md:rounded-xl md:border md:p-5 max-w-3xl mx-auto"
    >
      {/* Mobile: column with full-width text — a row squeezes the legal
              message into a tall narrow wrap that buries the page CTA
              (judge round 3). Desktop keeps the row card. */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
        <p className="text-xs leading-snug md:text-sm text-muted-foreground flex-1">
          {text.message}{' '}
          <Link
            to={localePath('/privacy', lang)}
            className="inline-flex min-h-11 items-center text-foreground underline underline-offset-4 hover:no-underline md:min-h-0 md:text-primary"
          >
            {text.privacy}
          </Link>
        </p>
        <div className="flex gap-2">
          <Button size="sm" className="min-h-11 flex-1 md:min-h-8 md:flex-none" onClick={decline}>
            {text.decline}
          </Button>
          <Button size="sm" className="min-h-11 flex-1 md:min-h-8 md:flex-none" onClick={accept}>
            {text.accept}
          </Button>
        </div>
      </div>
    </m.div>
  );
}
